// components/dashboard/widgets/ComparativoCursos.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty, Table } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { useDashboardData } from '../../../hooks/useDashboardData';

const ComparativoCursos = ({ endpoint, filtros, formato }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando comparativo...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar comparativo
        </p>
        <small style={{ color: '#cf1322' }}>
          {error?.message || String(error)}
        </small>
      </div>
    );
  }

  if (!dados || dados.length === 0) {
    return <Empty description="Nenhum dado de comparativo encontrado" />;
  }

  // Função para determinar performance
  const getPerformanceCurso = (item) => {
    const taxaRealizacao = parseFloat(item.taxa_realizacao) || 0;
    const notaMedia = parseFloat(item.nota_media) || 0;
    
    if (taxaRealizacao >= 80 && notaMedia >= 16) return 'excelente';
    if (taxaRealizacao >= 60 && notaMedia >= 14) return 'bom';
    if (taxaRealizacao >= 40 && notaMedia >= 12) return 'regular';
    return 'atencao';
  };

  // Formatar dados
  const dadosFormatados = dados
    .map((item) => {
      const cursoAbrev = item.curso
        ? item.curso.substring(0, 20) + (item.curso.length > 20 ? '...' : '')
        : 'N/A';

      return {
        curso: item.curso,
        curso_abrev: cursoAbrev,
        total_projetos: parseInt(item.total_projetos) || 0,
        total_defesas: parseInt(item.total_defesas) || 0,
        defesas_realizadas: parseInt(item.defesas_realizadas) || 0,
        taxa_realizacao: parseFloat(item.taxa_realizacao) || 0,
        nota_media: parseFloat(item.nota_media) || 0,
        tempo_medio_dias: parseFloat(item.tempo_medio_dias) || 0,
        performance: getPerformanceCurso(item),
      };
    })
    .sort((a, b) => b.taxa_realizacao - a.taxa_realizacao);

  // Cores baseadas na performance
  const getCor = (performance) => {
    const cores = {
      excelente: '#52c41a',
      bom: '#1890ff',
      regular: '#fadb14',
      atencao: '#f5222d',
    };
    return cores[performance] || '#8c8c8c';
  };

  // Estatísticas gerais
  const totalProjetos = dadosFormatados.reduce((sum, item) => sum + item.total_projetos, 0);
  const totalDefesas = dadosFormatados.reduce((sum, item) => sum + item.total_defesas, 0);
  const mediaNotas = dadosFormatados.reduce((sum, item) => sum + item.nota_media, 0) / dadosFormatados.length;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: '#fff',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '250px',
          }}
        >
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>
            📚 {data.curso}
          </p>
          <div style={{ fontSize: 12 }}>
            <p style={{ margin: '4px 0', color: '#1890ff' }}>
              📊 Taxa de Realização: <strong>{data.taxa_realizacao.toFixed(1)}%</strong>
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>
              📝 Projetos: {data.total_projetos}
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>
              🎯 Defesas: {data.defesas_realizadas}/{data.total_defesas}
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>
              ⭐ Nota Média: {data.nota_media.toFixed(1)}
            </p>
            {data.tempo_medio_dias > 0 && (
              <p style={{ margin: '4px 0', color: '#666' }}>
                ⏰ Tempo Médio: {data.tempo_medio_dias.toFixed(0)} dias
              </p>
            )}
            <div
              style={{
                marginTop: 8,
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: getCor(data.performance) + '20',
                color: getCor(data.performance),
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 'bold',
              }}
            >
              {data.performance.toUpperCase()}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };
CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        curso: PropTypes.string,
        taxa_realizacao: PropTypes.number,
        total_projetos: PropTypes.number,
        defesas_realizadas: PropTypes.number,
        total_defesas: PropTypes.number,
        nota_media: PropTypes.number,
        tempo_medio_dias: PropTypes.number,
        performance: PropTypes.string
      })
    })
  )
};
  // Formato tabela
  if (formato === 'tabela') {
    const colunas = [
      {
        title: 'Curso',
        dataIndex: 'curso',
        key: 'curso',
        width: '30%',
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getCor(record.performance),
              }}
            />
            <span style={{ fontWeight: 'bold', fontSize: 13 }}>{text}</span>
          </div>
        ),
      },
      {
        title: 'Projetos',
        dataIndex: 'total_projetos',
        key: 'projetos',
        align: 'center',
        width: '10%',
      },
      {
        title: 'Defesas',
        key: 'defesas',
        align: 'center',
        width: '15%',
        render: (_, record) => (
          <span>
            {record.defesas_realizadas}/{record.total_defesas}
          </span>
        ),
      },
      {
        title: 'Taxa (%)',
        dataIndex: 'taxa_realizacao',
        key: 'taxa',
        align: 'center',
        width: '10%',
        render: (value) => (
          <span style={{ fontWeight: 'bold' }}>{value.toFixed(1)}%</span>
        ),
      },
      {
        title: 'Nota Média',
        dataIndex: 'nota_media',
        key: 'nota',
        align: 'center',
        width: '10%',
        render: (value) => (
          <span style={{ fontWeight: 'bold' }}>{value.toFixed(1)}</span>
        ),
      },
      {
        title: 'Tempo (dias)',
        dataIndex: 'tempo_medio_dias',
        key: 'tempo',
        align: 'center',
        width: '15%',
        render: (value) => (
          <span>{value > 0 ? value.toFixed(0) : '-'}</span>
        ),
      },
      {
        title: 'Performance',
        dataIndex: 'performance',
        key: 'performance',
        align: 'center',
        width: '10%',
        render: (performance) => (
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: getCor(performance),
              color: 'white',
              fontSize: 10,
              fontWeight: 'bold',
            }}
          >
            {performance.toUpperCase()}
          </span>
        ),
      },
    ];

    return (
      <div style={{ height: 350 }}>
        {/* Estatísticas no topo */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginBottom: 16,
            padding: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            fontSize: 12,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{totalProjetos}</div>
            <div style={{ color: '#666' }}>Total Projetos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#52c41a' }}>{totalDefesas}</div>
            <div style={{ color: '#666' }}>Total Defesas</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
              {mediaNotas.toFixed(1)}
            </div>
            <div style={{ color: '#666' }}>Média Notas</div>
          </div>
        </div>

        <Table
          dataSource={dadosFormatados}
          columns={colunas}
          pagination={false}
          size="small"
          scroll={{ y: 250 }}
          rowKey="curso"
        />
      </div>
    );
  }

  // Formato gráfico (padrão)
  return (
    <div style={{ height: 350 }}>
      {/* Estatísticas no topo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          marginBottom: 16,
          padding: '8px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          fontSize: 12,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{dadosFormatados.length}</div>
          <div style={{ color: '#666' }}>Cursos</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
            {(dadosFormatados.reduce((sum, item) => sum + item.taxa_realizacao, 0) / dadosFormatados.length).toFixed(1)}%
          </div>
          <div style={{ color: '#666' }}>Taxa Média</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
            {mediaNotas.toFixed(1)}
          </div>
          <div style={{ color: '#666' }}>Nota Média</div>
        </div>
      </div>

      <div style={{ width: '100%', height: 'calc(350px - 90px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dadosFormatados}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="curso_abrev"
              tick={{ fontSize: 11 }}
              stroke="#666"
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#666"
              label={{ value: 'Taxa (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="taxa_realizacao"
              name="Taxa de Realização (%)"
              radius={[4, 4, 0, 0]}
            >
              {dadosFormatados.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getCor(entry.performance)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legenda de performance */}
      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: '#666',
          display: 'flex',
          justifyContent: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#52c41a' }} />
          Excelente (≥80% + ≥16)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1890ff' }} />
          Bom (≥60% + ≥14)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fadb14' }} />
          Regular (≥40% + ≥12)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f5222d' }} />
          Atenção (&lt;40% ou &lt;12)
        </span>
      </div>
    </div>
  );
};

// PropTypes
ComparativoCursos.displayName = 'ComparativoCursos';
ComparativoCursos.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  formato: PropTypes.oneOf(['grafico', 'tabela']),
};

ComparativoCursos.defaultProps = {
  filtros: {},
  formato: 'grafico',
};

export default ComparativoCursos;